// utils/exportar.js
// Helpers para exportar dados de relatórios em PDF e Excel.
//
// Bibliotecas: jspdf + jspdf-autotable + xlsx (SheetJS)
// Instalação:  npm install jspdf jspdf-autotable xlsx

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// ============================================================
// Helpers
// ============================================================

function dataFormatadaArquivo() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function nomeArquivo(prefixo, ext) {
  const slug = prefixo
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `${slug}_${dataFormatadaArquivo()}.${ext}`;
}

// ============================================================
// Exportar para PDF
// ============================================================
// titulo        - Título no topo do PDF
// subtitulo     - Linha de filtros aplicados / período (opcional)
// colunas       - Array de { header, dataKey } - ex: [{ header: 'Produto', dataKey: 'nome' }]
// dados         - Array de objetos com as mesmas chaves
// orientacao    - 'portrait' (padrão) ou 'landscape'
// ============================================================
export function exportarPDF({
  titulo,
  subtitulo = '',
  colunas,
  dados,
  orientacao = 'portrait',
}) {
  const doc = new jsPDF({
    orientation: orientacao,
    unit: 'mm',
    format: 'a4',
  });

  // Cabeçalho
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(titulo, 14, 16);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  const subtituloFinal = [
    subtitulo,
    `Gerado em ${new Date().toLocaleString('pt-BR')}`,
    `Total: ${dados.length} ${dados.length === 1 ? 'registro' : 'registros'}`,
  ].filter(Boolean).join(' · ');
  doc.text(subtituloFinal, 14, 22);

  doc.setTextColor(0);

  // Tabela
  autoTable(doc, {
    startY: 28,
    head: [colunas.map((c) => c.header)],
    body: dados.map((linha) => colunas.map((c) => {
      const v = linha[c.dataKey];
      if (v === null || v === undefined) return '';
      return String(v);
    })),
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [181, 16, 19], // #B51013 - vermelho Jatear
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250],
    },
    margin: { top: 28, left: 14, right: 14 },
  });

  // Rodapé com número da página
  const totalPaginas = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPaginas; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Página ${i} de ${totalPaginas} - Jatear Tratamento Anticorrosivo`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'center' }
    );
  }

  doc.save(nomeArquivo(titulo, 'pdf'));
}

// ============================================================
// Exportar para Excel (XLSX)
// ============================================================
export function exportarExcel({
  titulo,
  colunas,
  dados,
  nomeAba = 'Relatorio',
}) {
  const headers = colunas.map((c) => c.header);
  const linhas = dados.map((linha) =>
    colunas.map((c) => {
      const v = linha[c.dataKey];
      if (v === null || v === undefined) return '';
      return v;
    })
  );

  const wsData = [headers, ...linhas];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Ajusta largura das colunas automaticamente
  const colWidths = colunas.map((c) => {
    const maxLen = Math.max(
      c.header.length,
      ...dados.map((d) => String(d[c.dataKey] ?? '').length)
    );
    return { wch: Math.min(maxLen + 2, 50) };
  });
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, nomeAba.substring(0, 31));

  XLSX.writeFile(wb, nomeArquivo(titulo, 'xlsx'));
}

// ============================================================
// Helpers de formatação para uso nos relatórios
// ============================================================

export function formatarData(ts) {
  if (!ts) return '';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('pt-BR');
  } catch {
    return '';
  }
}

export function formatarDataHora(ts) {
  if (!ts) return '';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return d.toLocaleString('pt-BR');
  } catch {
    return '';
  }
}

export function formatarQtd(n, unidade = '') {
  if (n === null || n === undefined) return '';
  const num = Number(n);
  if (Number.isNaN(num)) return '';
  return unidade ? `${num} ${unidade}` : String(num);
}
